import { useMemo, useState } from "react";
import { Calculator, IndianRupee, Calendar, Percent, RotateCcw } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useI18n } from "@/lib/i18n";

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

function calcEMI(p, annualRate, months) {
  if (!p || !annualRate || !months) return 0;
  const r = annualRate / 12 / 100;
  if (r === 0) return p / months;
  return (p * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
}

export default function EmiCalculator() {
  const { t } = useI18n();
  const [amount, setAmount] = useState(500000); // ₹5L
  const [rate, setRate] = useState(11);
  const [tenure, setTenure] = useState(36); // months

  const emi = useMemo(() => calcEMI(amount, rate, tenure), [amount, rate, tenure]);
  const totalPayable = emi * tenure;
  const totalInterest = totalPayable - amount;
  const principalShare = (amount / totalPayable) * 100;
  const interestShare = 100 - principalShare;

  const reset = () => {
    setAmount(500000);
    setRate(11);
    setTenure(36);
  };

  return (
    <section
      id="emi"
      data-testid="emi-calculator"
      className="relative bg-[#FAF6EC] text-[#0C1530] section-pad overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-32 -left-20 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/12 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-20 w-[420px] h-[420px] rounded-full bg-[#0A1F44]/8 blur-3xl" />

      <div className="relative max-w-7xl mx-auto gutter">
        <Reveal>
          <div className="text-center">
            <span className="gold-divider text-[11px] tracking-[0.32em] uppercase text-[#B68F1E]">
              {t("emi.eyebrow")}
            </span>
            <h2
              data-testid="emi-heading"
              className="font-display fluid-h1 mt-5 tracking-tight"
            >
              {t("emi.heading")}
            </h2>
            <p className="mt-4 text-[#0C1530]/70 max-w-2xl mx-auto fluid-lead">
              {t("emi.sub")}
            </p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-10 md:mt-14 grid lg:grid-cols-12 gap-6 md:gap-8">
            {/* Sliders */}
            <div className="lg:col-span-7 min-w-0 bg-white rounded-3xl p-5 sm:p-7 md:p-10 border border-[#0A1F44]/10 shadow-[0_20px_60px_-30px_rgba(10,31,68,0.35)]">
              {/* Loan Amount */}
              <div>
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <label className="text-sm font-semibold tracking-wide text-[#0C1530]/75 flex items-center gap-2">
                    <IndianRupee size={14} className="text-[#B68F1E]" />
                    {t("emi.label.amount")}
                  </label>
                  <span
                    data-testid="emi-amount-value"
                    className="font-display text-xl text-[#0A1F44]"
                  >
                    {INR.format(amount)}
                  </span>
                </div>
                <input
                  data-testid="emi-amount-slider"
                  aria-label={`${t("emi.label.amount")} — currently ${INR.format(amount)}`}
                  type="range"
                  min={50000}
                  max={500000000}
                  step={50000}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="w-full mt-3 accent-[#D4AF37]"
                />
                <div className="flex justify-between text-[11px] text-[#0C1530]/50 mt-1">
                  <span>₹50,000</span>
                  <span>₹50 Cr</span>
                </div>
              </div>

              {/* Interest Rate */}
              <div className="mt-8">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <label className="text-sm font-semibold tracking-wide text-[#0C1530]/75 flex items-center gap-2">
                    <Percent size={14} className="text-[#B68F1E]" />
                    {t("emi.label.interest")}
                  </label>
                  <span
                    data-testid="emi-rate-value"
                    className="font-display text-xl text-[#0A1F44]"
                  >
                    {rate.toFixed(2)}%
                  </span>
                </div>
                <input
                  data-testid="emi-rate-slider"
                  aria-label={`${t("emi.label.interest")} — currently ${rate.toFixed(2)} percent`}
                  type="range"
                  min={6.5}
                  max={15}
                  step={0.25}
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  className="w-full mt-3 accent-[#D4AF37]"
                />
                <div className="flex justify-between text-[11px] text-[#0C1530]/50 mt-1">
                  <span>6.5%</span>
                  <span>15%</span>
                </div>
              </div>

              {/* Tenure */}
              <div className="mt-8">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <label className="text-sm font-semibold tracking-wide text-[#0C1530]/75 flex items-center gap-2">
                    <Calendar size={14} className="text-[#B68F1E]" />
                    {t("emi.label.tenure")}
                  </label>
                  <span
                    data-testid="emi-tenure-value"
                    className="font-display text-xl text-[#0A1F44]"
                  >
                    {tenure} mo · {(tenure / 12).toFixed(1)} yrs
                  </span>
                </div>
                <input
                  data-testid="emi-tenure-slider"
                  aria-label={`${t("emi.label.tenure")} — currently ${tenure} months`}
                  type="range"
                  min={6}
                  max={360}
                  step={6}
                  value={tenure}
                  onChange={(e) => setTenure(Number(e.target.value))}
                  className="w-full mt-3 accent-[#D4AF37]"
                />
                <div className="flex justify-between text-[11px] text-[#0C1530]/50 mt-1">
                  <span>6 mo</span>
                  <span>30 yrs</span>
                </div>
              </div>

              {/* Reset button */}
              <button
                data-testid="emi-reset"
                onClick={reset}
                className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#0A1F44]/15 text-[#0A1F44]/70 hover:text-[#0A1F44] hover:border-[#D4AF37] text-sm transition"
              >
                <RotateCcw size={14} /> Reset to default
              </button>
            </div>

            {/* Result card */}
            <div className="lg:col-span-5 min-w-0">
              <div className="relative bg-[#0A1F44] rounded-3xl p-5 sm:p-7 md:p-10 text-white overflow-hidden noise-overlay h-full">
                <div className="pointer-events-none absolute -top-12 -right-12 w-64 h-64 rounded-full bg-[#D4AF37]/15 blur-3xl" />
                <div className="flex items-center gap-3">
                  <Calculator size={18} className="text-[#D4AF37] shrink-0" />
                  <span className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">
                    {t("emi.result.monthly")}
                  </span>
                </div>
                <div
                  data-testid="emi-result"
                  className="mt-3 font-display text-[length:clamp(1.75rem,9vw,3.75rem)] lg:text-[length:clamp(2rem,3.4vw,3.75rem)] text-[#F0D788] leading-tight wrap-anywhere"
                >
                  {INR.format(Math.round(emi))}
                </div>

                {/* Progress / share bar */}
                <div className="mt-7">
                  <div className="flex h-2 rounded-full overflow-hidden bg-white/10">
                    <div
                      className="h-full bg-[#D4AF37]"
                      style={{ width: `${principalShare}%` }}
                      data-testid="emi-share-principal"
                    />
                    <div
                      className="h-full bg-white/50"
                      style={{ width: `${interestShare}%` }}
                      data-testid="emi-share-interest"
                    />
                  </div>
                  <div className="mt-3 flex flex-wrap justify-between gap-x-4 gap-y-1 text-[11px] text-white/60">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                      {t("emi.result.principal")} {principalShare.toFixed(0)}%
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-white/50" />
                      {t("emi.result.interest")} {interestShare.toFixed(0)}%
                    </span>
                  </div>
                </div>

                <div className="mt-7 grid grid-cols-1 min-[400px]:grid-cols-2 gap-4">
                  <div
                    data-testid="emi-total-interest"
                    className="rounded-xl p-4 border border-[#D4AF37]/25 bg-white/[0.04] min-w-0"
                  >
                    <div className="text-[10px] tracking-wider uppercase text-white/55">
                      {t("emi.result.interest")}
                    </div>
                    <div className="font-display text-xl md:text-2xl text-[#F0D788] mt-1 wrap-anywhere">
                      {INR.format(Math.round(totalInterest))}
                    </div>
                  </div>
                  <div
                    data-testid="emi-total-payable"
                    className="rounded-xl p-4 border border-[#D4AF37]/25 bg-white/[0.04] min-w-0"
                  >
                    <div className="text-[10px] tracking-wider uppercase text-white/55">
                      {t("emi.result.total")}
                    </div>
                    <div className="font-display text-xl md:text-2xl text-white mt-1 wrap-anywhere">
                      {INR.format(Math.round(totalPayable))}
                    </div>
                  </div>
                </div>

                <a
                  data-testid="emi-apply-cta"
                  href="/apply/loan"
                  className="mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold text-sm font-semibold"
                >
                  {t("emi.cta")}
                </a>
                <p className="mt-3 text-[11px] text-white/45 leading-relaxed">
                  * Illustrative only. Actual rates &amp; EMI may vary based on credit assessment,
                  loan product and tenure.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
